import { useState, useEffect, useCallback, useRef } from 'react';
import { useClient } from '../../../hooks/useClient';

const CLIENTS_ITEMS_PER_PAGE_KEY = 'dashboard.clients.itemsPerPage';
const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_ITEMS_PER_PAGE = 25;

const getStoredItemsPerPage = () => {
  try {
    const stored = localStorage.getItem(CLIENTS_ITEMS_PER_PAGE_KEY);
    const value = stored ? parseInt(stored, 10) : DEFAULT_ITEMS_PER_PAGE;
    return ITEMS_PER_PAGE_OPTIONS.includes(value) ? value : DEFAULT_ITEMS_PER_PAGE;
  } catch {
    return DEFAULT_ITEMS_PER_PAGE;
  }
};

const useClients = () => {
  const { getClientsByFilter } = useClient();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  // Pagination state (itemsPerPage persisted in localStorage)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getStoredItemsPerPage);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Stats por estatus (activo/inactivo/pendiente) desde el servidor para que coincidan con el total
  const [stats, setStats] = useState({ active: 0, inactive: 0, pending: 0 });
  const abortControllerRef = useRef(null);

  // Función para mapear datos de la API al formato esperado
  const mapClientData = (client) => {
    return {
      ...client,
      // Ensure all expected fields exist
      IdCliente: client.IdCliente || client.id || '',
      Nombre: client.Nombre || '',
      Apellidos: client.Apellidos || '',
      Email: client.Email || client.email || '',
      Telefono: client.Telefono || client.phoneNumber || '',
      IdEstatus: client.IdEstatus || client.status || 1,
      IdTipoCliente: client.IdTipoCliente || client.clientType || 1,
      NombrePlataforma: client.NombrePlataforma || '',
      NombrePlataformaSec: client.NombrePlataformaSec || '',
      CompanyName: client.CompanyName || '',
    };
  };

  // Cargar datos reales de la API con paginación, búsqueda y filtros del servidor
  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      setError(null);
      
      // Cancelar petición anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Crear nuevo AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;
      
      try {
        // Preparar filtros para el servidor
        const serverFilters = {
          currentPage,
          itemsPerPage,
        };

        // Validar y agregar clientId si es un número válido
        if (filters.clientId) {
          const parsedClientId = parseInt(filters.clientId, 10);
          if (!isNaN(parsedClientId) && parsedClientId > 0) {
            serverFilters.clientId = parsedClientId;
          }
        }

        // Agregar otros filtros de texto
        if (filters.name) serverFilters.name = filters.name;
        if (filters.lastName) serverFilters.lastName = filters.lastName;
        if (filters.email) serverFilters.email = filters.email;
        if (filters.userName) serverFilters.userName = filters.userName;
        if (filters.phoneNumber) serverFilters.phoneNumber = filters.phoneNumber;
        if (filters.externalId) serverFilters.externalId = filters.externalId;
        
        // Agregar filtro de status si existe
        if (filters.status) {
          const parsedStatus = parseInt(filters.status, 10);
          if (!isNaN(parsedStatus) && parsedStatus > 0) {
            serverFilters.statusId = parsedStatus;
          }
        }

        // Agregar filtro de clientType si existe
        if (filters.clientType) {
          const parsedClientType = parseInt(filters.clientType, 10);
          if (!isNaN(parsedClientType) && parsedClientType > 0) {
            serverFilters.clientType = parsedClientType;
          }
        }

        // Agregar filtro de rol (IdConfRoles) si existe
        if (filters.role) {
          const parsedRole = parseInt(filters.role, 10);
          if (!isNaN(parsedRole) && parsedRole > 0) {
            // Enviar como confRoleId para que el backend filtre por IdConfRoles
            serverFilters.confRoleId = parsedRole;
          }
        }

        const response = await getClientsByFilter(
          abortControllerRef.current.signal,
          serverFilters
        );
        
        // La respuesta tiene la estructura: { itemsFind: [], totalItems, totalPages, itemsPerPage, currentPage, totalActive, totalInactive, totalPending }
        let clientsList = response?.itemsFind || [];
        const total = response?.totalItems || 0;
        const pages = response?.totalPages || Math.ceil((response?.totalItems || 0) / itemsPerPage) || 1;
        setStats({
          active: response?.totalActive ?? 0,
          inactive: response?.totalInactive ?? 0,
          pending: response?.totalPending ?? 0,
        });
        
        // Mapear los datos al formato esperado
        const mappedClients = clientsList.map(mapClientData);
        
        // Aplicar ordenamiento client-side si es necesario
        let sortedClients = mappedClients;
        if (sortConfig.key) {
          sortedClients = [...mappedClients].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Manejar valores nulos/undefined
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Comparación de strings
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }
        
        setClients(sortedClients);
        setTotalItems(total);
        setTotalPages(pages);
        } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading clients:', err);
          setError(err.message || 'Error al cargar los clientes');
          setClients([]);
          setTotalItems(0);
          setTotalPages(1);
          setStats({ active: 0, inactive: 0, pending: 0 });
        }
      } finally {
        // Only the request that was not superseded may clear the flag. An
        // aborted one used to switch it off on its way out, so the skeleton
        // disappeared while its replacement was still in flight and the table
        // sat blank for a few hundred milliseconds before the rows landed.
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadClients();

    // Cleanup: cancelar petición si el componente se desmonta
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [getClientsByFilter, currentPage, itemsPerPage, filters, sortConfig]);

  const handleSearch = useCallback((term, selectedField = 'name') => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset a primera página cuando cambia la búsqueda
    
    if (term && term.trim()) {
      const trimmedTerm = term.trim();
      // Buscar solo en el campo específico seleccionado
      setFilters(prev => {
        const newFilters = {};
        // Limpiar todos los demás filtros y usar solo el campo seleccionado
        newFilters[selectedField] = trimmedTerm;
        return newFilters;
      });
    } else {
      // Si está vacío, limpiar todos los filtros
      setFilters({});
    }
  }, []);

  const handleFilter = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset a primera página cuando cambian filtros
  }, []);

  const handleStatusChange = useCallback((status) => {
    setCurrentPage(1);
    if (status && status !== 'all') {
      setFilters(prev => ({ ...prev, status }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.status;
        return newFilters;
      });
    }
  }, []);

  const handleSort = useCallback((key, direction) => {
    setSortConfig({ key, direction });
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
    try {
      localStorage.setItem(CLIENTS_ITEMS_PER_PAGE_KEY, String(items));
    } catch {
      // ignore localStorage errors (e.g. private mode)
    }
  }, []);

  return {
    clients,
    loading,
    error,
    pagination: {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      stats,
    },
    filters,
    searchTerm,
    handleSearch,
    handleFilter,
    handleStatusChange,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
  };
};

export default useClients;
